import * as qiniu from 'qiniu-js'
import { daysFormat } from '@/filters'
import { guid } from '@/utils/getCalculatedValue'
import { resolveApiBaseURL } from '@/utils/siteBrand'

export const VIDEO_UPLOAD_URL = '#'
const IMAGE_SKIP_BYTES = 400 * 1024
const VIDEO_SKIP_BYTES = 2 * 1024 * 1024
const IMAGE_MAX_EDGE = 1920
const VIDEO_MAX_WIDTH = 1280
const VIDEO_BITS_PER_SECOND = 800000
const AUDIO_BITS_PER_SECOND = 64000

export function toHttpsPlayUrl(url) {
  if (!url || typeof url !== 'string') return url
  if (url.indexOf('http://') === 0) return 'https://' + url.slice(7)
  return url
}

export function qiniuVideoSourceType(url, fallback) {
  const path = String(url || '').split('?')[0].toLowerCase()
  if (path.indexOf('.mp4') >= 0) return 'video/mp4'
  if (path.indexOf('.m3u8') >= 0) return 'application/x-mpegURL'
  return fallback || 'application/x-mpegURL'
}

export function normalizeHlsRequestUri(uri) {
  if (!uri || typeof uri !== 'string') return uri
  try {
    const origin =
      typeof window !== 'undefined' && window.location && window.location.origin
        ? window.location.origin
        : resolveApiBaseURL()
    const u = new URL(uri, origin)
    const path = u.pathname || ''
    if (
      /(^|\/)hls(\.m3u8)?$/i.test(path) &&
      path.indexOf('/organize/qiniu/') < 0
    ) {
      u.pathname = '/organize/qiniu/hls.m3u8'
      return u.toString()
    }
    if (/(^|\/)hlsfile$/i.test(path) && path.indexOf('/organize/qiniu/') < 0) {
      u.pathname = '/organize/qiniu/hlsfile'
      return u.toString()
    }
  } catch (e) {
    return uri
  }
  return uri
}

export function normalizeQiniuUid(uid) {
  if (Array.isArray(uid)) {
    return uid.find(Boolean) || ''
  }
  if (uid && typeof uid === 'object') {
    return uid.uid || uid.videoUid || ''
  }
  return uid || ''
}

export function getRawFile(file) {
  if (!file) return file
  return file.raw ? file.raw : file
}

export function getUploadEndpoints(state) {
  return {
    image: state.uploadImage,
    imageBase64: state.uploadImageBase64,
    signature: state.uploadSignature,
    face: state.uploadFaceimage,
    file: state.uploadfile,
    video: VIDEO_UPLOAD_URL
  }
}

export function findQiniuBucket(state, formKey) {
  const list = state.bucket || []
  const found = list.find((item) => item.value === formKey)
  return found && found.bucket ? found.bucket : 'dpn-video'
}

export function getVideoUidFromFile(file) {
  if (!file) return ''
  if (file.videoUid) return String(file.videoUid)
  if (file.res && file.res.videoUid) return String(file.res.videoUid)
  const uid = file.uid
  if (typeof uid === 'string' && uid.length >= 32 && uid.indexOf('-') > 0) {
    return uid
  }
  return ''
}

export function isQiniuVideoFile(file, field) {
  const fileType = field && field.fileType
  const key = field && field.key
  if (fileType === 'video') return true
  if (key === 'trainingVideos' || key === 'checkVideos') return true
  if (file && (file.videoUid || (file.res && file.res.videoUid))) return true
  const type = (file && (file.fileType || file.type)) || ''
  return type === 'application/x-mpegURL' || type.indexOf('video/') === 0
}

export function collectRowVideoUids(row) {
  if (!row) return []
  const uids = []
  const pushValue = (value) => {
    if (value == null || value === '') return
    if (Array.isArray(value)) {
      value.forEach(pushValue)
      return
    }
    if (typeof value !== 'string') {
      pushValue(String(value))
      return
    }
    const text = value.trim()
    if (!text || text === '{}' || text === '[]' || text === 'null') return
    if (
      (text.startsWith('{') && text.endsWith('}')) ||
      (text.startsWith('[') && text.endsWith(']'))
    ) {
      const inner = text.slice(1, -1).trim()
      if (!inner) return
      inner.split(',').forEach((item) => {
        const uid = item.trim().replace(/^["']|["']$/g, '')
        if (uid) uids.push(uid)
      })
      return
    }
    uids.push(text)
  }
  pushValue(row.trainingVideos)
  pushValue(row.checkVideos)
  if (row.dataFormat === 'video') {
    pushValue(row.associatedFiles)
  }
  return uids.filter((uid, index, list) => uid && list.indexOf(uid) === index)
}

export function isVideoFile(file, fileType) {
  if (fileType === 'video') return true
  const type = (file && file.type) || ''
  const name = (file && file.name) || ''
  return (
    type.indexOf('video/') === 0 ||
    /\.(mp4|mov|avi|mkv|webm|m4v|3gp|wmv)$/i.test(name)
  )
}

export const VIDEO_MAX_DURATION = 60
export const VIDEO_DURATION_SLACK = 3

export function normalizeVideoSeconds(duration) {
  const n = Number(duration)
  if (!isFinite(n) || n <= 0) return 0
  if (n > 1000) return n / 1000
  return n
}

export function isVideoTooLong(duration) {
  return (
    normalizeVideoSeconds(duration) > VIDEO_MAX_DURATION + VIDEO_DURATION_SLACK
  )
}

export function shouldLimitVideoDuration(field) {
  if (!field) return false
  return (
    field.fileType === 'video' ||
    field.key === 'checkVideos' ||
    field.key === 'trainingVideos'
  )
}

export function probeVideoDuration(file) {
  return new Promise((resolve) => {
    const raw = getRawFile(file)
    if (!raw || typeof URL === 'undefined' || typeof document === 'undefined') {
      resolve(0)
      return
    }
    let url = ''
    try {
      url = URL.createObjectURL(raw)
    } catch (e) {
      resolve(0)
      return
    }
    const video = document.createElement('video')
    video.preload = 'metadata'
    const done = (value) => {
      try {
        URL.revokeObjectURL(url)
      } catch (e) {
        /* ignore */
      }
      resolve(value)
    }
    video.onloadedmetadata = () => {
      const d = Number(video.duration)
      done(isFinite(d) && d > 0 ? d : 0)
    }
    video.onerror = () => done(0)
    setTimeout(() => done(0), 8000)
    video.src = url
  })
}

export function captureVideoPoster(file) {
  return new Promise((resolve) => {
    const raw = getRawFile(file)
    if (!raw || typeof URL === 'undefined' || typeof document === 'undefined') {
      resolve('')
      return
    }
    let url = ''
    try {
      url = URL.createObjectURL(raw)
    } catch (e) {
      resolve('')
      return
    }
    const video = document.createElement('video')
    video.preload = 'auto'
    video.muted = true
    video.playsInline = true
    const done = (value) => {
      try {
        URL.revokeObjectURL(url)
      } catch (e) {
        /* ignore */
      }
      resolve(value || '')
    }
    video.onloadeddata = () => {
      try {
        if (video.videoWidth < 1 || video.videoHeight < 1) {
          done('')
          return
        }
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        done(canvas.toDataURL('image/jpeg', 0.7))
      } catch (e) {
        done('')
      }
    }
    video.onerror = () => done('')
    setTimeout(() => done(''), 8000)
    video.src = url
    try {
      video.currentTime = 0.1
    } catch (e) {
      /* ignore */
    }
  })
}

export function isImageFile(file, fileType) {
  if (fileType && fileType !== 'image' && fileType !== 'picture') {
    return false
  }
  const type = (file && file.type) || ''
  const name = (file && file.name) || ''
  return type.indexOf('image/') === 0 || /\.(png|jpe?g|bmp|webp)$/i.test(name)
}

function replaceExtension(name, ext) {
  if (!name) return 'file.' + ext
  return name.replace(/\.[^.]+$/, '') + '.' + ext
}

export function getUploadedFileUid(item, endpoints) {
  if (!item || !item.res) return ''
  const res = item.res
  if (item.url === VIDEO_UPLOAD_URL) {
    return res.videoUid || ''
  }
  const data = res.data || {}
  if (item.url === endpoints.image || item.url === endpoints.imageBase64) {
    return data.imageId || ''
  }
  if (item.url === endpoints.signature) {
    return data.signatureId || ''
  }
  if (item.url === endpoints.face) {
    return data.faceId || ''
  }
  if (item.url === endpoints.file) {
    return data.fileUid || data.imageId || ''
  }
  return data.imageId || data.faceId || data.signatureId || data.fileUid || ''
}

export function compressImage(file, options) {
  const opts = options || {}
  if (!file || !isImageFile(file, opts.fileType)) {
    return Promise.resolve(file)
  }
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return Promise.resolve(file)
  }
  const skipBytes = opts.skipBytes != null ? opts.skipBytes : IMAGE_SKIP_BYTES
  if (!opts.force && file.size && file.size < skipBytes) {
    return Promise.resolve(file)
  }
  const maxEdge = opts.maxEdge || IMAGE_MAX_EDGE
  const quality = opts.quality || 0.82
  return loadImageElement(file)
    .then((image) => {
      let width = image.naturalWidth || image.width
      let height = image.naturalHeight || image.height
      const scale = Math.min(1, maxEdge / width, maxEdge / height)
      width = Math.round(width * scale)
      height = Math.round(height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, 0, 0, width, height)
      const keepPng = !opts.toJpeg && file.type === 'image/png'
      const mimeType = keepPng ? 'image/png' : 'image/jpeg'
      return canvasToBlob(canvas, mimeType, keepPng ? undefined : quality).then(
        (blob) => {
          if (!blob || blob.size >= file.size) return file
          const ext = keepPng ? 'png' : 'jpg'
          return new File([blob], replaceExtension(file.name, ext), {
            type: mimeType,
            lastModified: file.lastModified || Date.now()
          })
        }
      )
    })
    .catch(() => file)
}

export function formatFileSize(bytes) {
  const size = Number(bytes) || 0
  if (size < 1024) return size + 'B'
  if (size < 1024 * 1024) return (size / 1024).toFixed(1) + 'KB'
  return (size / (1024 * 1024)).toFixed(1) + 'MB'
}

export async function compressVideo(file, options) {
  const opts = options || {}
  if (!file || !isVideoFile(file, opts.fileType)) {
    return file
  }
  if (file.size && file.size <= VIDEO_SKIP_BYTES) {
    return file
  }
  let error = ''
  try {
    const result = await compressWithMediabunny(file, opts)
    if (isSmallerFile(result, file)) return markCompress(result, '')
    error = '转码完成但体积未减小'
  } catch (err) {
    error = (err && err.message) || 'WebCodecs转码失败'
    console.warn('mediabunny video compress failed', err)
  }
  try {
    const result = await compressWithMediaRecorder(file, opts)
    if (isSmallerFile(result, file)) return markCompress(result, '')
    error = error || '录制压缩后体积未减小'
  } catch (err) {
    error = (err && err.message) || error || '录制压缩失败'
    console.warn('MediaRecorder video compress failed', err)
  }
  return markCompress(file, error)
}

function markCompress(file, error) {
  try {
    file._compressError = error || ''
  } catch (e) {
    /* ignore */
  }
  return file
}

function isSmallerFile(next, original) {
  return !!(next && next.size && original && next.size < original.size)
}

function discardedReason(conversion) {
  const list = (conversion && conversion.discardedTracks) || []
  return list
    .map((item) => item.reason)
    .filter((reason) => reason && reason !== 'discarded_by_user')
    .join(',')
}

async function loadMediabunny() {
  const mod = await import('mediabunny')
  if (mod && mod.Conversion) return mod
  if (mod && mod.default && mod.default.Conversion) return mod.default
  throw new Error('视频压缩库加载失败')
}

async function compressWithMediabunny(file, opts) {
  const mb = await loadMediabunny()
  const bitrate = opts.videoBitsPerSecond || VIDEO_BITS_PER_SECOND
  const quality = new mb.Quality({
    bitrate: bitrate,
    bitrateMode: 'constant'
  })
  const videoCodec = await mb.getFirstEncodableVideoCodec(['avc', 'vp9', 'vp8'])
  const useWebm = videoCodec === 'vp8' || videoCodec === 'vp9'
  const createIO = () => ({
    input: new mb.Input({
      source: new mb.BlobSource(file),
      formats: mb.ALL_FORMATS
    }),
    output: new mb.Output({
      format: useWebm
        ? new mb.WebMOutputFormat()
        : new mb.Mp4OutputFormat({ fastStart: 'in-memory' }),
      target: new mb.BufferTarget()
    })
  })
  const videoOptions = {
    width: opts.maxWidth || VIDEO_MAX_WIDTH,
    quality: quality,
    forceTranscode: true,
    frameRate: 24
  }
  if (videoCodec) {
    videoOptions.codec = videoCodec
  }
  let io = createIO()
  let conversion = await mb.Conversion.init({
    input: io.input,
    output: io.output,
    video: videoOptions,
    audio: {
      quality: new mb.Quality({
        bitrate: opts.audioBitsPerSecond || AUDIO_BITS_PER_SECOND
      })
    },
    showWarnings: false
  })
  if (!conversion.isValid) {
    io = createIO()
    conversion = await mb.Conversion.init({
      input: io.input,
      output: io.output,
      video: videoOptions,
      audio: { discard: true },
      showWarnings: false
    })
  }
  if (!conversion.isValid) {
    throw new Error(discardedReason(conversion) || '视频无法转码')
  }
  conversion.onProgress = (progress) => {
    if (typeof opts.onProgress === 'function') {
      opts.onProgress(progress)
    }
  }
  await conversion.execute()
  const buffer = io.output.target && io.output.target.buffer
  if (!buffer || !buffer.byteLength) {
    throw new Error('转码结果为空')
  }
  const ext = useWebm ? 'webm' : 'mp4'
  return new File([buffer], replaceExtension(file.name, ext), {
    type: useWebm ? 'video/webm' : 'video/mp4',
    lastModified: file.lastModified || Date.now()
  })
}

function compressWithMediaRecorder(file, options) {
  const opts = options || {}
  const mimeType = pickRecorderMime()
  if (typeof MediaRecorder === 'undefined' || !mimeType) {
    return Promise.reject(new Error('浏览器不支持视频压缩'))
  }
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const objectUrl = URL.createObjectURL(file)
    let finished = false
    let timer = null
    let recorder = null
    const cleanup = () => {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      URL.revokeObjectURL(objectUrl)
      if (video.parentNode) {
        video.parentNode.removeChild(video)
      }
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
    const fail = (msg) => {
      if (finished) return
      finished = true
      try {
        if (recorder && recorder.state === 'recording') {
          recorder.stop()
        }
      } catch (e) {
        /* ignore */
      }
      cleanup()
      reject(new Error(msg || '录制压缩失败'))
    }
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', 'true')
    video.preload = 'auto'
    video.controls = false
    video.crossOrigin = 'anonymous'
    video.style.cssText =
      'position:fixed;left:-99999px;top:-99999px;width:160px;height:90px;opacity:0.01;'
    document.body.appendChild(video)
    video.onerror = () => fail('视频无法解码播放')
    const readyTimer = setTimeout(() => fail('视频加载超时'), 15000)
    const startRecording = () => {
      const capture =
        (typeof video.captureStream === 'function' && video.captureStream()) ||
        (typeof video.mozCaptureStream === 'function' && video.mozCaptureStream())
      if (!capture) {
        fail('无法捕获视频流')
        return
      }
      const durationMs =
        isFinite(video.duration) && video.duration > 0
          ? video.duration * 1000 + 20000
          : 12 * 60 * 1000
      timer = setTimeout(() => fail('压缩超时'), Math.min(durationMs, 12 * 60 * 1000))
      try {
        recorder = new MediaRecorder(capture, {
          mimeType: mimeType,
          videoBitsPerSecond: opts.videoBitsPerSecond || VIDEO_BITS_PER_SECOND,
          audioBitsPerSecond: opts.audioBitsPerSecond || AUDIO_BITS_PER_SECOND
        })
      } catch (e) {
        fail(e.message || '无法创建压缩器')
        return
      }
      const chunks = []
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size) {
          chunks.push(event.data)
        }
      }
      recorder.onerror = () => fail('压缩过程中断')
      recorder.onstop = () => {
        if (finished) return
        finished = true
        cleanup()
        const blob = new Blob(chunks, { type: mimeType.split(';')[0] })
        if (!blob.size) {
          reject(new Error('压缩结果为空'))
          return
        }
        const ext = mimeType.indexOf('mp4') >= 0 ? 'mp4' : 'webm'
        resolve(
          new File([blob], replaceExtension(file.name, ext), {
            type: blob.type,
            lastModified: file.lastModified || Date.now()
          })
        )
      }
      video.onended = () => {
        try {
          recorder.stop()
        } catch (e) {
          fail(e.message)
        }
      }
      const reportProgress = () => {
        if (finished || video.ended) return
        if (typeof opts.onProgress === 'function' && video.duration) {
          opts.onProgress(Math.min(0.99, video.currentTime / video.duration))
        }
        requestAnimationFrame(reportProgress)
      }
      try {
        recorder.start(1000)
        reportProgress()
      } catch (e) {
        fail(e.message)
      }
    }
    video.onloadeddata = () => {
      clearTimeout(readyTimer)
      const playPromise = video.play()
      Promise.resolve(playPromise)
        .then(startRecording)
        .catch(() => fail('视频无法播放'))
    }
    video.src = objectUrl
    video.load()
  })
}

export function compressForUpload(file, options) {
  const opts = options || {}
  if (opts.skipCompress) {
    return Promise.resolve(file)
  }
  if (isVideoFile(file, opts.fileType)) {
    return compressVideo(file, opts)
  }
  if (isImageFile(file, opts.fileType)) {
    return compressImage(file, opts)
  }
  return Promise.resolve(file)
}

export function uploadToQiniu(file, key, token, onProgress) {
  return new Promise((resolve, reject) => {
    if (!token) {
      reject(new Error('获取七牛上传凭证失败'))
      return
    }
    const observable = qiniu.upload(file, key, token)
    const subscription = observable.subscribe({
      next(res) {
        if (typeof onProgress === 'function' && res.total) {
          onProgress(res.total.percent / 100)
        }
      },
      error(err) {
        try {
          subscription.unsubscribe()
        } catch (e) {
          /* ignore */
        }
        reject(err)
      },
      complete(res) {
        resolve(res)
      }
    })
  })
}

export async function uploadQiniuVideo({
  file,
  bucket,
  dispatch,
  onUploadProgress
}) {
  const raw = getRawFile(file)
  const suffix = (raw.name || 'mp4').split('.').pop().toLowerCase()
  const currentDay = daysFormat(new Date()).replace(/-/g, '/')
  const fileUid = guid()
  const key = currentDay + '/' + fileUid + '.' + suffix
  const token = await dispatch('getQiniuToken', {
    bucket: bucket,
    expires: '10000',
    policy: {}
  })
  if (!token || typeof token !== 'string') {
    throw new Error('获取七牛上传凭证失败')
  }
  const res = await uploadToQiniu(raw, key, token, onUploadProgress)
  res.code = 200
  res.videoUid = fileUid
  res.key = res.key || key
  await dispatch('postQiniuFile', {
    uid: fileUid,
    videoUrl: currentDay,
    videoRealName: raw.name,
    videoType: suffix,
    tPartLink: key,
    bucket: bucket
  })
  return res
}

export async function uploadServerFile({ dispatch, url, file }) {
  const prepared = getRawFile(file)
  const formData = new FormData()
  formData.append('file', prepared)
  return dispatch('uploadFile', {
    url: url,
    data: formData
  })
}

export async function uploadPendingFiles({
  items,
  endpoints,
  dispatch,
  uploadVideo
}) {
  const list = items || []
  const finished = []
  for (let i = 0; i < list.length; i++) {
    const item = list[i]
    const raw = getRawFile(item.file)
    try {
      if (item.url === VIDEO_UPLOAD_URL) {
        if (typeof uploadVideo !== 'function') {
          item.res = { code: 500, msg: '当前表单不支持视频上传' }
        } else {
          item.res = await uploadVideo(raw, item)
        }
      } else if (item.url === endpoints.imageBase64) {
        item.res = await dispatch('uploadImgBase64', {
          base64Str: item.base64Url
        })
      } else {
        item.res = await uploadServerFile({
          dispatch: dispatch,
          url: item.url,
          file: raw
        })
      }
    } catch (err) {
      item.res = {
        code: 500,
        msg: err && err.message ? err.message : '上传失败'
      }
    }
    if (!item.res) {
      item.res = { code: 500, msg: '上传失败' }
    }
    finished.push(item)
  }
  return finished
}

function pickRecorderMime() {
  const types = [
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9,opus',
    'video/webm',
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4'
  ]
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return ''
  }
  for (let i = 0; i < types.length; i++) {
    if (MediaRecorder.isTypeSupported(types[i])) {
      return types[i]
    }
  }
  return ''
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = (err) => {
      URL.revokeObjectURL(url)
      reject(err)
    }
    image.src = url
  })
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve) => {
    if (!canvas.toBlob) {
      resolve(null)
      return
    }
    canvas.toBlob(
      (blob) => resolve(blob),
      mimeType,
      quality
    )
  })
}
